import { AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { C } from "./homeTheme";
import {
  FortAgentActivateResultCode,
  FortAgentPayMixedFusdResultCode,
  MyFortAgentItem,
} from "@/api/types";
import { formatEtherWithDecimals, formatThousands } from "@/lib/utils";
import { useFortAgentActivateSign } from "@/hooks/api/useFortAgentActivateSign";
import { useFortAgentPayMixedFusd } from "@/hooks/api/useFortAgentPayMixedFusd";
import { showError, showSuccess } from "@/lib/toast";
import { useConnection, useWriteContract } from "wagmi";
import {
  simulateContract,
  switchChain,
  waitForTransactionReceipt,
} from "@wagmi/core";
import { wagmiConfig } from "@/config";
import {
  CCC_FUSD_ADDRESS,
  FORTTRADE_CONTRACT_ADDRESS,
  USDT_ADDRESS,
} from "@/config/env";
import { FortTradeAbi } from "@/abi/FortTrade.abi";
import { parseChainError } from "@/lib/chainError";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { getTokenName } from "@/config/common";
import { bscChainId } from "@/config/wagmi";

/**
 * 混合购延期 FUSD 未扣款时：先签名补缴，再链上激活（与转让弹窗的补缴说明区一致）。
 */
export function PayFusdActivateDialog({
  open,
  onClose,
  agent,
}: {
  open: boolean;
  onClose: () => void;
  agent: MyFortAgentItem;
}) {
  const { t } = useTranslation();
  const { address: walletAddress } = useConnection();
  const queryClient = useQueryClient();
  const writeContract = useWriteContract();
  const { mutateAsync: payMixedFusdAsync } = useFortAgentPayMixedFusd();
  const { mutateAsync: activateSignAsync } = useFortAgentActivateSign();
  const [processing, setProcessing] = useState(false);

  const mustPayFusd =
    agent.paymentMode === "mixed" &&
    !agent.fusdPurchaseDeducted &&
    BigInt(agent.purchasePayFusdWei || "0") > 0n;

  const handleClose = () => {
    if (!processing) onClose();
  };

  const failAndClose = () => {
    queryClient.invalidateQueries({ queryKey: ["myFortAgents"] });
    onClose();
  };

  const handlePayAndActivate = async () => {
    if (!walletAddress) {
      showError(t("toast.common.connectWalletFirst"));
      return;
    }
    if (!mustPayFusd) {
      onClose();
      return;
    }
    await switchChain(wagmiConfig, { chainId: bscChainId });
    setProcessing(true);
    try {
      const payRes = await payMixedFusdAsync({ agentId: agent.agentId });
      if (payRes.resultCode !== FortAgentPayMixedFusdResultCode.Success) {
        failAndClose();
        return;
      }

      const actRes = await activateSignAsync({ agentId: agent.agentId });
      if (actRes.resultCode !== FortAgentActivateResultCode.Success) {
        failAndClose();
        return;
      }

      const { request } = await simulateContract(wagmiConfig, {
        address: FORTTRADE_CONTRACT_ADDRESS as `0x${string}`,
        abi: FortTradeAbi,
        functionName: "activate",
        args: [
          BigInt(actRes.agentId),
          BigInt(actRes.deadline),
          BigInt(actRes.nonce),
          actRes.signature as `0x${string}`,
        ],
      });
      const hash = await writeContract.mutateAsync(request);
      await waitForTransactionReceipt(wagmiConfig, { hash });
      showSuccess(t("toast.fortAgent.activate.result.success"));
      queryClient.invalidateQueries({ queryKey: ["myFortAgents"] });
      onClose();
    } catch (err) {
      console.error("payFusdActivateErr", err);
      showError(t(parseChainError(err)));
      failAndClose();
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && handleClose()}>
      <DialogContent
        className="max-w-sm mx-auto"
        style={{
          background: C.bg,
          border: `1px solid ${C.border}`,
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: C.primary }}>
            {t("homePage.payFusdActivateDialog.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div
            className="p-3 rounded-lg text-xs"
            style={{
              background: "rgba(245,158,11,0.06)",
              border: `1px solid rgba(245,158,11,0.1)`,
              color: C.amber,
            }}
          >
            <div className="flex items-center gap-2 font-semibold mb-1">
              <AlertCircle size={14} />
              {t("homePage.transferDialog.fusdPendingTitle", {
                tokenName: getTokenName(CCC_FUSD_ADDRESS),
              })}
            </div>
            <p>
              {t("homePage.transferDialog.fusdPendingDesc", {
                amount: formatThousands(
                  formatEtherWithDecimals(agent.purchasePayFusdWei)
                ),
                tokenName: getTokenName(CCC_FUSD_ADDRESS),
                tokenNameUsdt: getTokenName(USDT_ADDRESS),
              })}
            </p>
          </div>
          <div
            className="rounded-lg p-3"
            style={{ background: C.card2, border: `1px solid ${C.border}` }}
          >
            <div className="flex justify-between text-sm">
              <span style={{ color: C.muted }}>
                {t("homePage.transferDialog.fusdPayLabel", {
                  tokenName: getTokenName(CCC_FUSD_ADDRESS),
                })}
              </span>
              <span className="mono-num font-bold" style={{ color: C.amber }}>
                {formatThousands(
                  formatEtherWithDecimals(agent.purchasePayFusdWei)
                )}{" "}
                {getTokenName(CCC_FUSD_ADDRESS)}
              </span>
            </div>
          </div>
          {!agent.fusdPurchaseDeducted && +agent.purchasePayFusdWei > 0 && (
            <div
              className="rounded-lg p-3 space-y-2 text-xs"
              style={{
                background: "rgba(245,158,11,0.06)",
                border: `1px solid rgba(245,158,11,0.2)`,
              }}
            >
              <div
                className="flex items-center gap-1.5 font-semibold mb-2"
                style={{ color: C.amber }}
              >
                <AlertCircle size={14} />
                {t("homePage.transferDialog.fusdDetailTitle", {
                  tokenName: getTokenName(CCC_FUSD_ADDRESS),
                })}
              </div>
              <div
                className="space-y-1.5 text-[11px]"
                style={{ color: C.text }}
              >
                <div className="flex justify-between">
                  <span>{t("homePage.transferDialog.fusdTotal")}</span>
                  <span
                    className="mono-num font-semibold"
                    style={{ color: C.amber }}
                  >
                    {formatThousands(
                      formatEtherWithDecimals(agent.purchasePayFusdWei)
                    )}{" "}
                    {getTokenName(CCC_FUSD_ADDRESS)}
                  </span>
                </div>
                <div
                  className="flex justify-between"
                  style={{ color: C.muted }}
                >
                  <span>
                    {t("homePage.transferDialog.fusdFromGame", {
                      tokenName: getTokenName(CCC_FUSD_ADDRESS),
                    })}
                  </span>
                  <span className="mono-num">
                    {formatThousands(
                      formatEtherWithDecimals(
                        BigInt(agent.purchasePayFusdFromGameBalanceWei)
                      )
                    )}
                    {getTokenName(CCC_FUSD_ADDRESS)}
                  </span>
                </div>
                <div
                  className="flex justify-between"
                  style={{ color: C.muted }}
                >
                  <span>
                    {t("homePage.transferDialog.fusdFromWithdraw", {
                      tokenName: getTokenName(CCC_FUSD_ADDRESS),
                    })}
                  </span>
                  <span className="mono-num">
                    {formatThousands(
                      formatEtherWithDecimals(
                        BigInt(agent.purchasePayFusdFromWaitClaimWei)
                      )
                    )}
                    {getTokenName(CCC_FUSD_ADDRESS)}
                  </span>
                </div>
              </div>
            </div>
          )}
          <button
            type="button"
            className="w-full py-2.5 rounded-lg font-bold text-sm btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={processing}
            onClick={handlePayAndActivate}
          >
            {processing
              ? t("homePage.payFusdActivateDialog.processing")
              : t("homePage.payFusdActivateDialog.confirm")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
